import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaArrowRight, FaShoppingCart, FaTimes, FaTrash } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import QuantityStepper from '../QuantityStepper';
import { useCart } from '../../contexts/CartContext';
import config from '../../../../config/config';
import { formatPrice } from '../../utils/formatPrice';
import './CartDrawer.css';

const PLACEHOLDER = '/images/no-image.webp';

/**
 * Slide-in cart panel. The context opens it whenever a line is added, so the
 * add-to-cart action has a visible result beyond the badge ticking up.
 */
const CartDrawer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const {
    lines,
    itemCount,
    subtotal,
    setQty,
    removeLine,
    isDrawerOpen,
    closeDrawer,
  } = useCart();

  const panelRef = useRef<HTMLDivElement>(null);
  const isRTL = i18n.language?.split('-')[0] === 'ar';

  // Escape closes, and the page behind must not scroll while the panel is open.
  useEffect(() => {
    if (!isDrawerOpen) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDrawer();
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    panelRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isDrawerOpen, closeDrawer]);

  const currency = lines[0]?.currency || 'MAD';
  // Slide in from whichever side the panel is pinned to.
  const offscreen = isRTL ? '-100%' : '100%';

  return (
    <AnimatePresence>
      {isDrawerOpen && (
        <>
          <motion.div
            className="cart-drawer__overlay"
            onClick={closeDrawer}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          />

          <motion.aside
            className="cart-drawer"
            ref={panelRef}
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-label={t('shop.cart.title')}
            initial={{ x: offscreen }}
            animate={{ x: 0 }}
            exit={{ x: offscreen }}
            transition={{ type: 'tween', duration: 0.28, ease: 'easeOut' }}
          >
            <header className="cart-drawer__header">
              <h2 className="cart-drawer__title">
                <FaShoppingCart aria-hidden="true" />
                {t('shop.cart.title')}
                {itemCount > 0 && (
                  <span className="cart-drawer__count">
                    {t('shop.cart.drawerCount', { count: itemCount })}
                  </span>
                )}
              </h2>
              <button
                type="button"
                className="cart-drawer__close"
                onClick={closeDrawer}
                aria-label={t('shop.cart.close')}
              >
                <FaTimes aria-hidden="true" />
              </button>
            </header>

            {lines.length === 0 ? (
              <div className="cart-drawer__empty">
                <p>{t('shop.cart.empty')}</p>
                <Link to="/boutique" onClick={closeDrawer} className="cart-drawer__link">
                  {t('shop.backToShop')}
                </Link>
              </div>
            ) : (
              <>
                <ul className="cart-drawer__lines">
                  {lines.map((line) => (
                    <li key={line.productId} className="cart-drawer__line">
                      <Link
                        to={`/boutique/${line.slug}`}
                        onClick={closeDrawer}
                        className="cart-drawer__media"
                      >
                        <img
                          src={line.image || PLACEHOLDER}
                          alt={line.name}
                          loading="lazy"
                        />
                      </Link>

                      <div className="cart-drawer__info">
                        <Link
                          to={`/boutique/${line.slug}`}
                          onClick={closeDrawer}
                          className="cart-drawer__name"
                        >
                          {line.name}
                        </Link>
                        {config.shop.showPrices && (
                          <span className="cart-drawer__unit">
                            {formatPrice(line.unitPrice, line.currency)}{' '}
                            {t('shop.cart.unitEach')}
                          </span>
                        )}

                        <div className="cart-drawer__controls">
                          <QuantityStepper
                            value={line.qty}
                            onChange={(qty) => setQty(line.productId, qty)}
                            size="sm"
                          />
                          <button
                            type="button"
                            className="cart-drawer__remove"
                            onClick={() => removeLine(line.productId)}
                            aria-label={t('shop.cart.removeItem', { name: line.name })}
                            title={t('shop.cart.remove')}
                          >
                            <FaTrash aria-hidden="true" />
                          </button>
                        </div>
                      </div>

                      {config.shop.showPrices && (
                        <span className="cart-drawer__line-total">
                          {formatPrice(line.unitPrice * line.qty, line.currency)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>

                <footer className="cart-drawer__footer">
                  {config.shop.showPrices && (
                    <div className="cart-drawer__subtotal">
                      <span>{t('shop.cart.subtotal')}</span>
                      <strong>{formatPrice(subtotal, currency)}</strong>
                    </div>
                  )}

                  <Link
                    to="/boutique/commande"
                    onClick={closeDrawer}
                    className="cart-drawer__checkout"
                  >
                    {t('shop.cart.checkout')} <FaArrowRight aria-hidden="true" />
                  </Link>

                  <Link
                    to="/boutique/panier"
                    onClick={closeDrawer}
                    className="cart-drawer__view"
                  >
                    {t('shop.cart.viewCart')}
                  </Link>

                  <button
                    type="button"
                    className="cart-drawer__continue"
                    onClick={closeDrawer}
                  >
                    {t('shop.cart.continue')}
                  </button>
                </footer>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
